import {
  formatParisDateTime,
  LEGACY_MIGRATION_STATUS,
  RECHARGE_ADMIN_URL,
  RECHARGE_FEATURE_MODE,
  type LegacyMigrationDetail,
} from '@flux/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CircleAlert, TriangleAlert } from 'lucide-react';
import { useState, type JSX } from 'react';
import { Badge, StatusBadge } from '@/components/ui/badge.js';
import { DescriptionItem, DescriptionList } from '@/components/ui/description-list.js';
import { ErrorState } from '@/components/ui/error-state.js';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table.js';
import { Button } from '@/components/ui/button.js';
import { ConfirmDialog } from '@/components/ui/confirm-dialog.js';
import { ApiError } from '@/lib/api/client.js';
import { toast } from '@/lib/toast.js';
import { queryKeys } from '@/lib/query/keys.js';
import { fetchLegacyMigration, rollbackLegacyMigration } from './api.js';
import {
  Dash,
  DetailSection,
  DetailSkeleton,
  DetailSummary,
  ExternalRechargeLink,
  SlackNotification,
} from './detail-parts.js';
import { DetailSheet } from './detail-sheet.js';
import { shortBundleName } from './migration-format.js';
import { formatEuros, formatSignedEuros } from './money.js';

function Euros({ value, signed = false }: { value: number | null; signed?: boolean }): JSX.Element {
  const formatted = signed ? formatSignedEuros(value) : formatEuros(value);
  return formatted === null ? <Dash /> : <span className="whitespace-nowrap tabular-nums">{formatted}</span>;
}

function Tier({ bundle }: { bundle: LegacyMigrationDetail['bundle'] }): JSX.Element {
  if (bundle === null) {
    return <Dash />;
  }
  return (
    <span>
      {shortBundleName(bundle.name)} · <span className="tabular-nums">{`${String(bundle.percent)}\u00A0%`}</span>
    </span>
  );
}

function CompositionTable({ migration }: { migration: LegacyMigrationDetail }): JSX.Element {
  if (migration.items.length === 0) {
    return <p className="text-sm text-muted-foreground">Aucun produit reconnu dans la composition</p>;
  }
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Produit</TableHead>
          <TableHead>Alias</TableHead>
          <TableHead className="text-right">Quantité</TableHead>
          <TableHead className="text-right">Prix unitaire (remise abonnement)</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {migration.items.map((item) => (
          <TableRow key={`${item.alias}-${item.product}`}>
            <TableCell>
              <span className="flex flex-wrap items-center gap-1.5">
                <span className={item.deleted ? 'text-muted-foreground line-through' : undefined}>
                  {item.title}
                </span>
                {item.deleted ? <Badge tone="gray">Supprimé du catalogue</Badge> : null}
              </span>
            </TableCell>
            <TableCell>
              <span className="break-all font-mono text-xs" translate="no">
                {item.alias}
              </span>
            </TableCell>
            <TableCell className="text-right tabular-nums">{item.quantity}</TableCell>
            <TableCell className="text-right">
              <Euros value={item.unitPrice} />
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function PriceCheck({ check }: { check: NonNullable<LegacyMigrationDetail['priceCheck']> }): JSX.Element {
  if (check.ok) {
    return (
      <p className="text-sm">
        {check.attempts > 0
          ? `Prix conformes après ${String(check.attempts)} correction${check.attempts > 1 ? 's' : ''}`
          : 'Prix conformes'}
      </p>
    );
  }
  return (
    <p className="flex items-start gap-2 rounded-md border border-status-orange-border bg-status-orange-bg px-3 py-2 text-sm text-status-orange-fg">
      <TriangleAlert className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
      <span>Prix des items non conformes après 3 corrections : à vérifier dans Recharge</span>
    </p>
  );
}

/**
 * « Annuler la migration » (`06` §5.6) : Flux remet chez Recharge l'abonnement
 * d'origine qu'il a sauvegardé avant la migration. Action externe à fort
 * impact (prix du client) : confirmation qui nomme l'abonnement et l'effet.
 */
function MigrationRollback({ migration }: { migration: LegacyMigrationDetail }): JSX.Element | null {
  const queryClient = useQueryClient();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const rollback = useMutation({
    mutationFn: () => rollbackLegacyMigration(migration.id),
    onSuccess: (updated) => {
      setConfirmOpen(false);
      queryClient.setQueryData(queryKeys.subscriptions.migration(migration.id), updated);
      void queryClient.invalidateQueries({ queryKey: ['subscriptions', 'migrations', 'list'] });
      toast.success('Migration annulée');
    },
    onError: () => {
      setConfirmOpen(false);
    },
  });

  if (migration.status === 'rolled_back') {
    return (
      <p className="rounded-md border border-border bg-muted px-3 py-2 text-sm text-muted-foreground">
        {`Migration annulée${migration.rolledBackAt === null ? '' : ` le ${formatParisDateTime(new Date(migration.rolledBackAt))}`}${
          migration.rolledBackBy === null ? '' : ` par ${migration.rolledBackBy.name}`
        }. L’abonnement a retrouvé son produit et sa composition d’origine ; une prochaine réactivation le migrera de nouveau.`}
      </p>
    );
  }
  if (!migration.rollbackAvailable) {
    return null;
  }
  const errorId = `migration-rollback-error-${migration.id}`;
  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => {
            rollback.reset();
            setConfirmOpen(true);
          }}
          {...(rollback.isError ? { 'aria-describedby': errorId } : {})}
        >
          Annuler la migration…
        </Button>
        <span className="text-sm text-muted-foreground">Remet l’abonnement d’origine chez Recharge</span>
      </div>
      {rollback.isError ? (
        <p id={errorId} role="alert" className="text-sm text-status-red-fg">
          {rollback.error instanceof ApiError ? rollback.error.message : 'Impossible d’annuler la migration. Réessayez.'}
        </p>
      ) : null}
      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title={`Annuler la migration de l’abonnement ${migration.subscriptionId} ?`}
        description="Flux supprime la sélection du bundle et remet le produit legacy, la quantité et la composition d’origine. Le client retrouve son prix legacy. S’il réactive de nouveau son abonnement, la migration se relancera."
        confirmLabel="Annuler la migration"
        cancelLabel="Garder la migration"
        isConfirming={rollback.isPending}
        onConfirm={() => {
          rollback.mutate();
        }}
      />
    </div>
  );
}

function MigrationDetailBody({ migration }: { migration: LegacyMigrationDetail }): JSX.Element {
  return (
    <div className="flex flex-col gap-4">
      {migration.failedStep === null ? null : (
        <p className="flex items-start gap-2 rounded-md border border-status-red-border bg-status-red-bg px-3 py-2 text-sm text-status-red-fg">
          <CircleAlert className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
          <span>{`Échec pendant : ${migration.failedStep}. ${migration.rollbackAvailable ? 'Annulez la migration pour remettre l’abonnement d’origine.' : 'Vérifiez l’abonnement dans Recharge.'}`}</span>
        </p>
      )}
      <MigrationRollback migration={migration} />

      <DetailSummary title="Résumé">
        <DescriptionList>
          <DescriptionItem label="Statut">
            <StatusBadge registry={LEGACY_MIGRATION_STATUS} code={migration.status} />
          </DescriptionItem>
          <DescriptionItem label="Raison">{migration.reason ?? <Dash />}</DescriptionItem>
          <DescriptionItem label="Mode">
            <StatusBadge registry={RECHARGE_FEATURE_MODE} code={migration.mode} />
          </DescriptionItem>
          <DescriptionItem label="Date">
            <span className="whitespace-nowrap tabular-nums">
              {formatParisDateTime(new Date(migration.createdAt))}
            </span>
          </DescriptionItem>
          <DescriptionItem label="Client">
            {migration.email === null ? <Dash /> : <span className="break-all">{migration.email}</span>}
          </DescriptionItem>
          <DescriptionItem label="Palier">
            <Tier bundle={migration.bundle} />
          </DescriptionItem>
        </DescriptionList>
      </DetailSummary>

      <DetailSection title="Composition">
        {migration.composition === null ? null : (
          <p className="text-sm text-muted-foreground">
            Composition d’origine :{' '}
            <span className="break-all font-mono text-xs text-foreground" translate="no">
              {migration.composition}
            </span>
          </p>
        )}
        <CompositionTable migration={migration} />
      </DetailSection>

      <DetailSection title="Calcul">
        <DescriptionList>
          <DescriptionItem label="Prix legacy">
            <Euros value={migration.legacyPrice} />
          </DescriptionItem>
          <DescriptionItem label="Prix legacy ajusté">
            <Euros value={migration.adjustedLegacyPrice} />
          </DescriptionItem>
          <DescriptionItem label="Total après remise abonnement (−10 %)">
            <Euros value={migration.totalAfterSubscriptionDiscount} />
          </DescriptionItem>
          <DescriptionItem label="Palier">
            {migration.bundle === null ? (
              <Dash />
            ) : (
              <span className="tabular-nums">{`${String(migration.bundle.percent)}\u00A0%`}</span>
            )}
          </DescriptionItem>
          <DescriptionItem label="Nouveau prix">
            <Euros value={migration.newPrice} />
          </DescriptionItem>
          <DescriptionItem label="Écart">
            <Euros value={migration.difference} signed />
          </DescriptionItem>
        </DescriptionList>
      </DetailSection>

      {migration.priceCheck === null ? null : (
        <DetailSection title="Vérification des prix">
          <PriceCheck check={migration.priceCheck} />
        </DetailSection>
      )}

      <DetailSection title="Liens">
        <ul className="flex flex-col gap-1.5">
          <li>
            <ExternalRechargeLink
              href={`${RECHARGE_ADMIN_URL}/subscriptions/${encodeURIComponent(migration.subscriptionId)}/details`}
            >
              {`Ouvrir l’abonnement ${migration.subscriptionId} dans Recharge`}
            </ExternalRechargeLink>
          </li>
          {migration.customerId === null ? null : (
            <li>
              <ExternalRechargeLink
                href={`${RECHARGE_ADMIN_URL}/customers/${encodeURIComponent(migration.customerId)}`}
              >
                Ouvrir le client dans Recharge
              </ExternalRechargeLink>
            </li>
          )}
        </ul>
      </DetailSection>

      <DetailSection title="Notification">
        {migration.slack === null ? (
          <p className="text-sm text-muted-foreground">Aucun message Slack pour cette migration</p>
        ) : (
          <SlackNotification slack={migration.slack} mode={migration.mode} />
        )}
      </DetailSection>
    </div>
  );
}

/**
 * Panneau de détail d'une migration legacy (`06` §8.3), ouvert par `?id=` :
 * composition, calcul du palier, vérification des prix, liens et trace Slack.
 */
export function MigrationDetailSheet({
  id,
  onClose,
}: {
  id: string | null;
  onClose: () => void;
}): JSX.Element {
  const query = useQuery({
    queryKey: queryKeys.subscriptions.migration(id ?? ''),
    queryFn: () => fetchLegacyMigration(id ?? ''),
    enabled: id !== null,
  });

  const migration = query.data;
  const error = query.error instanceof ApiError ? query.error : null;
  const isNotFound = error?.status === 404;

  return (
    <DetailSheet
      open={id !== null}
      onClose={onClose}
      title={migration ? `Migration · abonnement ${migration.subscriptionId}` : 'Migration'}
    >
      {query.isPending ? (
        <DetailSkeleton label="Chargement de la migration" />
      ) : query.isError ? (
        <ErrorState
          message={error?.message ?? 'Impossible de charger cette migration.'}
          detail={error?.detail}
          {...(isNotFound
            ? {}
            : {
                onRetry: () => {
                  void query.refetch();
                },
              })}
        />
      ) : (
        <MigrationDetailBody migration={query.data} />
      )}
    </DetailSheet>
  );
}
